// Compare two PDFs by their text: each page is read into lines (runs on one baseline, left to right), the two line lists are
// aligned with a longest-common-subsequence diff, and the differences are reported with the page they are on.

import { PdfDocument, renderPage, type PdfTextRun } from "@fg/web-core/pdf";

export interface PageLines { page: number; text: string }
export type Change =
    | { kind: "removed"; page: number; text: string }
    | { kind: "added"; page: number; text: string };

export interface CompareResult {
    changes: Change[];
    pagesA: number;
    pagesB: number;
    sameLines: number;
    /** The documents were large, so lines were compared one page at a time. */
    perPage: boolean;
}

const norm = (s: string) => s.replace(/\s+/g, " ").trim();

/** The lines of one page, top to bottom. */
export async function pageLines(doc: PdfDocument, index: number): Promise<string[]> {
    const page = doc.page(index);
    const [w, h] = page.size;
    const scale = 0.2;
    const cv = document.createElement("canvas");
    cv.width = Math.max(1, Math.ceil(w * scale));
    cv.height = Math.max(1, Math.ceil(h * scale));
    const text: PdfTextRun[] = [];
    await renderPage(doc, page, cv.getContext("2d")!, { scale, text, annotations: false });
    const rows: Array<{ y: number; size: number; parts: Array<{ x: number; s: string }> }> = [];
    for (const r of text) {
        const s = r.chars.join("");
        if (!s.trim()) continue;
        const row = rows.find((q) => Math.abs(q.y - r.y) < Math.max(2, r.size * 0.4));
        if (row) row.parts.push({ x: r.x, s });
        else rows.push({ y: r.y, size: r.size, parts: [{ x: r.x, s }] });
    }
    rows.sort((a, b) => a.y - b.y);
    return rows.map((r) => norm(r.parts.sort((a, b) => a.x - b.x).map((p) => p.s).join(" "))).filter(Boolean);
}

async function allLines(doc: PdfDocument, onProgress?: (done: number, total: number) => void): Promise<PageLines[][]> {
    const out: PageLines[][] = [];
    for (let i = 0; i < doc.numPages; i++) {
        out.push((await pageLines(doc, i)).map((text) => ({ page: i + 1, text })));
        onProgress?.(i + 1, doc.numPages);
    }
    return out;
}

/** The LCS alignment of two sequences as matched index pairs. */
function lcs(a: string[], b: string[]): Array<[number, number]> {
    const n = a.length, m = b.length;
    // trim a common head and tail first: most of a document usually matches
    let head = 0;
    while (head < n && head < m && a[head] === b[head]) head++;
    let tail = 0;
    while (tail < n - head && tail < m - head && a[n - 1 - tail] === b[m - 1 - tail]) tail++;
    const pairs: Array<[number, number]> = [];
    for (let i = 0; i < head; i++) pairs.push([i, i]);
    const an = n - head - tail, bm = m - head - tail;
    if (an > 0 && bm > 0) {
        const w = bm + 1;
        const t = new Uint16Array((an + 1) * w);
        for (let i = an - 1; i >= 0; i--) {
            for (let j = bm - 1; j >= 0; j--) {
                t[i * w + j] = a[head + i] === b[head + j] ? t[(i + 1) * w + j + 1]! + 1 : Math.max(t[(i + 1) * w + j]!, t[i * w + j + 1]!);
            }
        }
        let i = 0, j = 0;
        while (i < an && j < bm) {
            if (a[head + i] === b[head + j]) { pairs.push([head + i, head + j]); i++; j++; }
            else if (t[(i + 1) * w + j]! >= t[i * w + j + 1]!) i++;
            else j++;
        }
    }
    for (let k = 0; k < tail; k++) pairs.push([n - tail + k, m - tail + k]);
    return pairs;
}

function diff(a: PageLines[], b: PageLines[]): { changes: Change[]; same: number } {
    const pairs = lcs(a.map((x) => x.text), b.map((x) => x.text));
    const changes: Change[] = [];
    let i = 0, j = 0;
    for (const [pi, pj] of pairs) {
        while (i < pi) { changes.push({ kind: "removed", page: a[i]!.page, text: a[i]!.text }); i++; }
        while (j < pj) { changes.push({ kind: "added", page: b[j]!.page, text: b[j]!.text }); j++; }
        i++; j++;
    }
    while (i < a.length) { changes.push({ kind: "removed", page: a[i]!.page, text: a[i]!.text }); i++; }
    while (j < b.length) { changes.push({ kind: "added", page: b[j]!.page, text: b[j]!.text }); j++; }
    return { changes, same: pairs.length };
}

/** Compares the text of two documents. */
export async function comparePdfs(a: Uint8Array, b: Uint8Array, onProgress?: (what: string, done: number, total: number) => void): Promise<CompareResult> {
    const da = PdfDocument.open(a), db = PdfDocument.open(b);
    const la = await allLines(da, (d, t) => onProgress?.("first", d, t));
    const lb = await allLines(db, (d, t) => onProgress?.("second", d, t));
    const fa = la.flat(), fb = lb.flat();
    // two big documents would make the table huge: fall back to page against page
    if (fa.length * fb.length > 30_000_000) {
        const changes: Change[] = [];
        let same = 0;
        for (let p = 0; p < Math.max(la.length, lb.length); p++) {
            const r = diff(la[p] ?? [], lb[p] ?? []);
            changes.push(...r.changes);
            same += r.same;
        }
        return { changes, pagesA: da.numPages, pagesB: db.numPages, sameLines: same, perPage: true };
    }
    const r = diff(fa, fb);
    return { changes: r.changes, pagesA: da.numPages, pagesB: db.numPages, sameLines: r.same, perPage: false };
}
